import React from "react";
import {
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalHeader,
  MDBModalTitle,
  MDBModalBody,
  MDBModalFooter,
  MDBBtn,
  MDBIcon,
  MDBBadge,
  MDBTable,
  MDBTableHead,
  MDBTableBody,
  MDBSpinner,
} from "mdb-react-ui-kit";

import { doFetchJson } from "Projects/CC_cubi_clan_project/Utils/ConnectionManager";
import { toast } from "Projects/CC_cubi_clan_project/GlobalComponents/ToastHost/ToastHost";

import SellTicketModal from "./SellTicketModal";

/**
 * TicketsModal — listado de entradas de un lote, con filtro por estado,
 * venta individual y cancelación de venta.
 *
 * Props:
 *  - show
 *  - batch: TicketBatchDTO
 *  - onClose
 *  - onTicketChanged: () => void   // para que el caller refresque contadores
 */
class TicketsModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      tickets: [],
      loading: false,
      statusFilter: "ALL",
      sellTarget: null,
      cancelTarget: null,
      cancelReason: "",
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.show !== this.props.show && this.props.show) {
      this.setState({ statusFilter: "ALL" }, this.loadTickets);
    }
  }

  loadTickets = () => {
    const { batch } = this.props;
    if (!batch) return;
    const { statusFilter } = this.state;
    const params = ["idBatch=" + batch.idBatch];
    if (statusFilter !== "ALL") params.push("status=" + statusFilter);
    this.setState({ loading: true });
    doFetchJson("TEAN", "getTickets?" + params.join("&"))
      .then((json) => {
        if (json && json.STATE === "SUCCESS") {
          this.setState({ tickets: json.DATA || [] });
        } else {
          this.setState({ tickets: [] });
        }
      })
      .catch(() => this.setState({ tickets: [] }))
      .finally(() => this.setState({ loading: false }));
  };

  handleClose = () => {
    if (this.props.onClose) this.props.onClose();
  };

  handleStatusFilter = (e) => {
    this.setState({ statusFilter: e.target.value }, this.loadTickets);
  };

  openSell = (t) => this.setState({ sellTarget: t });
  closeSell = () => this.setState({ sellTarget: null });

  openCancel = (t) => this.setState({ cancelTarget: t, cancelReason: "" });
  closeCancel = () => this.setState({ cancelTarget: null, cancelReason: "" });

  reactivate = (t) => {
    doFetchJson("TEAN", "reactivateTicket/" + t.idTicket, { method: "PUT" })
      .then((json) => {
        if (json && json.STATE === "SUCCESS") {
          toast.show({ message: "Entrada reactivada", type: "success" });
          this.loadTickets();
          if (this.props.onTicketChanged) this.props.onTicketChanged();
        } else {
          toast.show({ message: (json && json.MESSAGE) || "No se pudo reactivar la entrada", type: "danger" });
        }
      })
      .catch(() => toast.show({ message: "Error de red", type: "danger" }));
  };

  confirmCancelSale = () => {
    const { cancelTarget, cancelReason } = this.state;
    if (!cancelTarget) return;
    const qs = cancelReason ? "?reason=" + encodeURIComponent(cancelReason) : "";
    doFetchJson("TEAN", "cancelTicketSale/" + cancelTarget.idTicket + qs, { method: "PUT" })
      .then((json) => {
        if (json && json.STATE === "SUCCESS") {
          toast.show({ message: "Venta cancelada", type: "success" });
          this.closeCancel();
          this.loadTickets();
          if (this.props.onTicketChanged) this.props.onTicketChanged();
        } else {
          toast.show({ message: (json && json.MESSAGE) || "No se pudo cancelar la venta", type: "danger" });
        }
      })
      .catch(() => toast.show({ message: "Error de red", type: "danger" }));
  };

  onSold = () => {
    this.closeSell();
    this.loadTickets();
    if (this.props.onTicketChanged) this.props.onTicketChanged();
  };

  renderStatus = (s) => {
    switch (s) {
      case "AVAILABLE":
        return <MDBBadge color="success">Disponible</MDBBadge>;
      case "RESERVED":
        return <MDBBadge color="warning">Reservada</MDBBadge>;
      case "SOLD":
        return <MDBBadge color="primary">Vendida</MDBBadge>;
      case "CANCELLED":
        return <MDBBadge color="danger">Cancelada</MDBBadge>;
      case "VOID":
        return <MDBBadge color="dark">Anulada</MDBBadge>;
      default:
        return <MDBBadge color="secondary">{s}</MDBBadge>;
    }
  };

  formatDateTime = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return iso;
      return d.toLocaleString("es-ES", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (e) {
      return iso;
    }
  };

  render() {
    const { show, batch, canManage } = this.props;
    const { tickets, loading, statusFilter, sellTarget, cancelTarget, cancelReason } = this.state;
    if (!batch) return null;

    return (
      <>
        <MDBModal show={show} onHide={this.handleClose} tabIndex="-1">
          <MDBModalDialog centered scrollable size="xl">
            <MDBModalContent>
              <MDBModalHeader>
                <MDBModalTitle>
                  <MDBIcon fas icon="list" className="me-2" />
                  Entradas del lote — <em>{batch.name}</em>
                </MDBModalTitle>
                <MDBBtn className="btn-close" color="none" onClick={this.handleClose} />
              </MDBModalHeader>
              <MDBModalBody>
                <div className="d-flex flex-wrap align-items-center justify-content-between mb-3">
                  <div>
                    <span className="me-2 small text-muted">Filtrar:</span>
                    <select
                      className="form-select form-select-sm d-inline-block"
                      style={{ width: "auto" }}
                      value={statusFilter}
                      onChange={this.handleStatusFilter}
                    >
                      <option value="ALL">Todas</option>
                      <option value="AVAILABLE">Disponibles</option>
                      <option value="SOLD">Vendidas</option>
                      <option value="CANCELLED">Canceladas</option>
                      <option value="RESERVED">Reservadas</option>
                    </select>
                  </div>
                  <div className="small text-muted">
                    {loading ? "Cargando..." : `${tickets.length} entrada(s)`}
                  </div>
                </div>

                {loading ? (
                  <div className="text-center py-4">
                    <MDBSpinner />
                  </div>
                ) : tickets.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    No hay entradas con este filtro.
                  </div>
                ) : (
                  <MDBTable small hover responsive>
                    <MDBTableHead>
                      <tr>
                        <th>#</th>
                        <th>Estado</th>
                        <th>Comprador</th>
                        {canManage && <th>Vendedor</th>}
                        <th>Vendida</th>
                        <th className="text-end">Precio</th>
                        <th className="text-end">Acciones</th>
                      </tr>
                    </MDBTableHead>
                    <MDBTableBody>
                      {tickets.map((t) => (
                        <tr key={t.idTicket}>
                          <td>
                            <strong>{t.ticketNumber || "—"}</strong>
                            <div className="text-muted" style={{ fontSize: "0.7rem" }}>
                              {t.uuid && t.uuid.substring(0, 8)}
                            </div>
                          </td>
                          <td>{this.renderStatus(t.status)}</td>
                          <td>
                            {t.status === "SOLD" || t.status === "CANCELLED" ? (
                              <>
                                <div>{t.customerName || t.soldToName || "—"}</div>
                                {t.soldToEmail && (
                                  <div className="text-muted small">{t.soldToEmail}</div>
                                )}
                              </>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          {canManage && (
                            <td className="small">
                              {t.soldByName || (t.soldBy ? `#${t.soldBy}` : "—")}
                            </td>
                          )}
                          <td className="small">{this.formatDateTime(t.soldAt)}</td>
                          <td className="text-end">
                            {t.pricePaid != null ? `${t.pricePaid} €` : "—"}
                          </td>
                          <td className="text-end">
                            {t.status === "AVAILABLE" && (
                              <MDBBtn size="sm" color="success" onClick={() => this.openSell(t)}>
                                <MDBIcon fas icon="cash-register" className="me-1" />
                                Vender
                              </MDBBtn>
                            )}
                            {t.status === "SOLD" && canManage && (
                              <MDBBtn size="sm" color="danger" outline onClick={() => this.openCancel(t)}>
                                <MDBIcon fas icon="undo" className="me-1" />
                                Revertir
                              </MDBBtn>
                            )}
                            {t.status === "CANCELLED" && canManage && (
                              <MDBBtn size="sm" color="secondary" outline onClick={() => this.reactivate(t)}>
                                <MDBIcon fas icon="redo" className="me-1" />
                                Reactivar
                              </MDBBtn>
                            )}
                          </td>
                        </tr>
                      ))}
                    </MDBTableBody>
                  </MDBTable>
                )}
              </MDBModalBody>
              <MDBModalFooter>
                <MDBBtn color="secondary" outline onClick={this.handleClose}>
                  Cerrar
                </MDBBtn>
              </MDBModalFooter>
            </MDBModalContent>
          </MDBModalDialog>
        </MDBModal>

        <SellTicketModal
          show={!!sellTarget}
          ticket={sellTarget}
          batchPrice={batch.price}
          onClose={this.closeSell}
          onSold={this.onSold}
        />

        <MDBModal show={!!cancelTarget} onHide={this.closeCancel} tabIndex="-1">
          <MDBModalDialog centered>
            <MDBModalContent>
              <MDBModalHeader>
                <MDBModalTitle>
                  <MDBIcon fas icon="undo" className="me-2 text-danger" />
                  Revertir venta
                </MDBModalTitle>
                <MDBBtn className="btn-close" color="none" onClick={this.closeCancel} />
              </MDBModalHeader>
              <MDBModalBody>
                <p>
                  ¿Seguro que quieres cancelar la venta de la entrada{" "}
                  <strong>#{cancelTarget ? cancelTarget.ticketNumber || cancelTarget.idTicket : ""}</strong>?
                </p>
                {cancelTarget && (cancelTarget.soldToName || cancelTarget.customerName) && (
                  <p className="text-muted small mb-2">
                    Vendida a: {cancelTarget.customerName || cancelTarget.soldToName}
                    {cancelTarget.soldToEmail && <> &lt;{cancelTarget.soldToEmail}&gt;</>}
                  </p>
                )}
                <div>
                  <label className="form-label small">Motivo (opcional)</label>
                  <input
                    className="form-control form-control-sm"
                    value={cancelReason}
                    onChange={(e) => this.setState({ cancelReason: e.target.value })}
                    placeholder="Motivo de la cancelación..."
                    maxLength={250}
                  />
                </div>
              </MDBModalBody>
              <MDBModalFooter>
                <MDBBtn color="secondary" outline onClick={this.closeCancel}>
                  No, mantener
                </MDBBtn>
                <MDBBtn color="danger" onClick={this.confirmCancelSale}>
                  <MDBIcon fas icon="undo" className="me-1" />
                  Sí, cancelar venta
                </MDBBtn>
              </MDBModalFooter>
            </MDBModalContent>
          </MDBModalDialog>
        </MDBModal>
      </>
    );
  }
}

export default TicketsModal;
